import { oc } from "@orpc/contract";
import { z } from "zod";

const UUIDSchema = z.uuid();

export const NotificationItemSchema = z.object({
  id: UUIDSchema,
  eventId: UUIDSchema,
  eventType: z.string(),
  actorId: UUIDSchema.nullable(),
  notifiableType: z.string().nullable(),
  notifiableId: UUIDSchema.nullable(),
  payload: z.record(z.string(), z.unknown()),
  createdAt: z.iso.datetime(),
  readAt: z.iso.datetime().nullable(),
  archivedAt: z.iso.datetime().nullable(),
});

export const ListNotificationsInputSchema = z.object({
  cursor: UUIDSchema.optional(),
  limit: z.int().min(1).max(100).default(20),
  unreadOnly: z.boolean().default(false),
  /**
   * Which archive view to return:
   *   - 'active' (default): rows where archived_at IS NULL — the bell's inbox.
   *   - 'archived': rows where archived_at IS NOT NULL — the history page.
   *   - 'all': both, for unified searches.
   */
  filter: z.enum(["active", "archived", "all"]).default("active"),
});

export const ListNotificationsOutputSchema = z.object({
  notifications: z.array(NotificationItemSchema),
  nextCursor: UUIDSchema.nullable(),
  hasMore: z.boolean(),
});

export const UnreadCountOutputSchema = z.object({
  count: z.int().nonnegative(),
});

export const MarkAsReadInputSchema = z.object({
  notificationId: UUIDSchema,
});

export const MarkAsReadOutputSchema = z.object({
  success: z.boolean(),
});

export const MarkAllAsReadOutputSchema = z.object({
  updatedCount: z.int().nonnegative(),
});

export const ArchiveInputSchema = z.object({
  notificationId: UUIDSchema,
});

export const ArchiveOutputSchema = z.object({
  success: z.boolean(),
});

export const UnarchiveInputSchema = z.object({
  notificationId: UUIDSchema,
});

export const UnarchiveOutputSchema = z.object({
  success: z.boolean(),
});

/**
 * Sender options exposed to workflow authors. The `default_sendgrid`
 * entry is the org-wide verified-sender; `microsoft_graph` entries are
 * the enabled UPNs from the email-ingest configs.
 */
export const NotificationSenderOptionSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("default_sendgrid"),
    label: z.string(),
    fromAddress: z.string(),
  }),
  z.object({
    kind: z.literal("microsoft_graph"),
    label: z.string(),
    upn: z.string(),
  }),
]);

export const ListSenderOptionsOutputSchema = z.object({
  options: z.array(NotificationSenderOptionSchema),
});

export const notificationsContracts = {
  list: oc
    .input(ListNotificationsInputSchema)
    .output(ListNotificationsOutputSchema)
    .route({ method: "GET" }),

  unreadCount: oc.output(UnreadCountOutputSchema).route({ method: "GET" }),

  markAsRead: oc
    .input(MarkAsReadInputSchema)
    .output(MarkAsReadOutputSchema)
    .route({ method: "POST" }),

  markAllAsRead: oc.output(MarkAllAsReadOutputSchema).route({ method: "POST" }),

  archive: oc
    .input(ArchiveInputSchema)
    .output(ArchiveOutputSchema)
    .route({ method: "POST" }),

  unarchive: oc
    .input(UnarchiveInputSchema)
    .output(UnarchiveOutputSchema)
    .route({ method: "POST" }),

  listSenderOptions: oc
    .output(ListSenderOptionsOutputSchema)
    .route({ method: "GET" }),
} as const;

export type NotificationSenderOption = z.infer<
  typeof NotificationSenderOptionSchema
>;
export type ListSenderOptionsOutput = z.infer<
  typeof ListSenderOptionsOutputSchema
>;
export type NotificationItem = z.infer<typeof NotificationItemSchema>;
export type ListNotificationsInput = z.infer<
  typeof ListNotificationsInputSchema
>;
export type ListNotificationsOutput = z.infer<
  typeof ListNotificationsOutputSchema
>;
